import type { JSX } from 'react';
import { Button, Group, SegmentedControl, Select, TextInput } from '@mantine/core';
import type { Benchmark } from '../../api';
import { BENCHMARK_FILTERS } from '../../format';
import { type BenchmarkFilter, NO_FILTER, activeCount, buildOf, kindOf, levelOf, matches, normalUnit, options } from './benchmarkFilter';

/**
 * The filter row above the catalog and the picker (AP-87). The choices of
 * each filter count what the other filters leave, so a combination that
 * finds nothing is not offered.
 */
export function BenchmarkFilterBar({ list, filter, onChange, groupLocked }: {
  list: readonly Benchmark[];
  filter: BenchmarkFilter;
  onChange: (f: BenchmarkFilter) => void;
  /** In the picker the cost group comes from the line; the field is then not offered. */
  groupLocked?: boolean;
}): JSX.Element {
  const without = (key: keyof BenchmarkFilter): Benchmark[] => list.filter((b) => matches(b, { ...filter, [key]: NO_FILTER[key], words: '' }));
  const set = (patch: Partial<BenchmarkFilter>): void => onChange({ ...filter, ...patch });
  const select = (key: 'buildingType' | 'unit' | 'edition' | 'region', pick: (b: Benchmark) => string | null, w: number): JSX.Element | null => {
    const data = options(without(key), pick);
    if (data.length === 0 && !filter[key]) return null;
    return (
      <Select size="xs" w={w} placeholder={BENCHMARK_FILTERS[key]} aria-label={BENCHMARK_FILTERS[key]} data={data} value={filter[key]} onChange={(v) => set({ [key]: v })}
        searchable clearable comboboxProps={{ withinPortal: true }} nothingFoundMessage="nichts gefunden" />
    );
  };
  const kinds = options(without('kind'), kindOf).map((o) => o.value);
  const builds = options(without('build'), (b) => buildOf(b) || null).map((o) => o.value);

  return (
    <Group gap="xs" wrap="wrap" align="center">
      <TextInput size="xs" placeholder={BENCHMARK_FILTERS.words} value={filter.words} onChange={(e) => set({ words: e.currentTarget.value })} style={{ flex: '1 1 220px' }} />
      {kinds.length > 1 || filter.kind ? (
        <SegmentedControl size="xs" value={filter.kind ?? 'alle'} onChange={(v) => set({ kind: v === 'alle' ? null : (v as BenchmarkFilter['kind']) })}
          data={[{ value: 'alle', label: 'alle Arten' }, ...['Gebäude', 'Bauelement', 'Position', 'sonstige'].filter((k) => kinds.includes(k) || filter.kind === k).map((k) => ({ value: k, label: BENCHMARK_FILTERS.kinds[k as keyof typeof BENCHMARK_FILTERS.kinds] }))]} />
      ) : null}
      {builds.length > 1 || filter.build ? (
        <SegmentedControl size="xs" value={filter.build ?? 'alle'} onChange={(v) => set({ build: v === 'alle' ? null : (v as BenchmarkFilter['build']) })}
          data={[{ value: 'alle', label: 'Neu- und Altbau' }, { value: 'Neubau', label: 'Neubau' }, { value: 'Altbau', label: 'Altbau' }]} />
      ) : null}
      {select('buildingType', (b) => b.buildingType, 240)}
      {!groupLocked && (
        <TextInput size="xs" w={130} placeholder={BENCHMARK_FILTERS.costGroup} aria-label={BENCHMARK_FILTERS.costGroup} value={filter.costGroup} onChange={(e) => set({ costGroup: e.currentTarget.value.replace(/[^\d.]/g, '') })} />
      )}
      {!groupLocked && (
        <Select size="xs" w={130} placeholder={BENCHMARK_FILTERS.level} aria-label={BENCHMARK_FILTERS.level} clearable comboboxProps={{ withinPortal: true }}
          data={options(without('level'), (b) => (levelOf(b.costGroup) ? String(levelOf(b.costGroup)) : null)).sort((a, b) => a.value.localeCompare(b.value)).map((o) => ({ value: o.value, label: `${BENCHMARK_FILTERS.levels[Number(o.value) as 1 | 2 | 3 | 4]} ${o.label.slice(o.value.length)}` }))}
          value={filter.level ? String(filter.level) : null} onChange={(v) => set({ level: v ? (Number(v) as BenchmarkFilter['level']) : null })} />
      )}
      {select('unit', (b) => normalUnit(b.unit) || null, 150)}
      {select('edition', (b) => b.edition, 230)}
      {select('region', (b) => b.region, 150)}
      {activeCount(filter) > 0 && <Button size="compact-xs" variant="subtle" onClick={() => onChange({ ...NO_FILTER, words: filter.words, ...(groupLocked ? { costGroup: filter.costGroup } : {}) })}>Filter zurücksetzen</Button>}
    </Group>
  );
}
